import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Spin } from 'antd';
import { BulbOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import classNames from 'classnames';
import { IAppComponentProps } from '../../../../../components';
import { getI18n } from '../../../../../utils/Utils';
import { QUERY_CFO_REPORTING } from '../CFOReportingDashboard/cfoReportingQueries';
import { QUERY_EIM_ACTIVE_PARTNER_COUNTRY_METRICS } from '../../dashboardQueries';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import { getRangePickerLocalization } from '../../../../info-portal/components/grid/CalendarUtils';
import styles from './bmwEinvoicingPerformanceDashboard.module.scss';

const { RangePicker } = DatePicker;

type CfoStatusRow = {
    status?: string | null;
    count?: number | null;
};

type CfoReportingData = {
    totalInvoices?: { value?: number | null } | null;
    statusBreakdown?: CfoStatusRow[] | null;
};

type CountryMetric = {
    coverage?: {
        activePartners?: number | null;
    } | null;
};

type CfoReportingQueryResult = {
    getCFOReporting?: CfoReportingData | null;
};

type CountryMetricsQueryResult = {
    allActivePartnerCountryMetrics?: CountryMetric[] | null;
};

type BmwPerformanceCacheSnapshot = {
    reportingData: CfoReportingData | null;
    totalReportingData: CfoReportingData | null;
    countryMetrics: CountryMetric[];
};

type BmwPerformanceCacheEntry = {
    updatedAt: number;
    snapshot: BmwPerformanceCacheSnapshot;
};

const DEFAULT_RANGE_DAYS = 30;
const BMW_EINVOICE_CACHE_PREFIX = 'bmwEinvoicingPerformanceDashboardCache:v1';
const BMW_EINVOICE_CACHE_TTL_MS = 10 * 60 * 1000;
type ThemeMode = 'dark' | 'light';

const toDateRange = (daysBack: number): [Dayjs, Dayjs] => {
    const end = dayjs();
    return [end.subtract(daysBack - 1, 'day').startOf('day'), end.endOf('day')];
};

const formatCompactNumber = (value?: number | null): string => {
    const numberValue = Number(value ?? 0);
    if (Math.abs(numberValue) >= 1000000) {
        return `${(numberValue / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(numberValue) >= 1000) {
        return `${(numberValue / 1000).toFixed(1)}k`;
    }
    return new Intl.NumberFormat().format(numberValue);
};

const getWidthBucket = (value: number): number => {
    const clampedValue = Math.max(0, Math.min(100, value));
    return Math.round(clampedValue / 10) * 10;
};

const BMWEinvoicingPerformanceDashboard: FunctionComponent<IAppComponentProps> = (props) => {
    const intl = props.intl;
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>(() => toDateRange(DEFAULT_RANGE_DAYS));
    const [theme, setTheme] = useState<ThemeMode>(() => {
        try {
            const stored = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
            if (stored === 'light' || stored === 'dark') {
                return stored;
            }
        } catch {
            // Ignore storage failures and fall back to the default theme.
        }

        return 'light';
    });
    const [reportingData, setReportingData] = useState<CfoReportingData | null>(null);
    const [totalReportingData, setTotalReportingData] = useState<CfoReportingData | null>(null);
    const [countryMetrics, setCountryMetrics] = useState<CountryMetric[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [getCfoReporting] = useLazyQuery<CfoReportingQueryResult>(QUERY_CFO_REPORTING);
    const [getCountryMetrics] = useLazyQuery<CountryMetricsQueryResult>(QUERY_EIM_ACTIVE_PARTNER_COUNTRY_METRICS);

    const toggleTheme = useCallback(() => {
        setTheme((currentTheme) => {
            const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
            try {
                globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, nextTheme);
            } catch {
                // Ignore storage failures.
            }
            return nextTheme;
        });
    }, []);

    const getCacheKey = useCallback((fromDate: Dayjs, toDate: Dayjs) => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        return `${BMW_EINVOICE_CACHE_PREFIX}:${locale}:${fromDate.format('YYYY-MM-DD')}:${toDate.format('YYYY-MM-DD')}`;
    }, []);

    const loadDashboard = useCallback(async (forceRefresh = false) => {
        const [fromDate, toDate] = selectedRange;
        const cacheKey = getCacheKey(fromDate, toDate);

        if (forceRefresh) {
            globalThis.sessionStorage.removeItem(cacheKey);
        }

        if (!forceRefresh) {
            const cached = globalThis.sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached) as BmwPerformanceCacheEntry;
                    const isFresh = Number.isFinite(parsed?.updatedAt)
                        && (Date.now() - parsed.updatedAt) <= BMW_EINVOICE_CACHE_TTL_MS;
                    if (isFresh) {
                        setReportingData(parsed.snapshot?.reportingData ?? null);
                        setTotalReportingData(parsed.snapshot?.totalReportingData ?? null);
                        setCountryMetrics(parsed.snapshot?.countryMetrics ?? []);
                        return;
                    }
                    globalThis.sessionStorage.removeItem(cacheKey);
                } catch {
                    globalThis.sessionStorage.removeItem(cacheKey);
                }
            }
        }

        setIsLoading(true);

        try {
            const [cfoResponse, totalCfoResponse, metricsResponse] = await Promise.all([
                getCfoReporting({
                    variables: {
                        fromDate: fromDate.format('YYYY-MM-DD'),
                        toDate: toDate.format('YYYY-MM-DD'),
                        companyCode: 'A1',
                    },
                    fetchPolicy: 'network-only',
                }),
                getCfoReporting({
                    variables: {
                        fromDate: fromDate.format('YYYY-MM-DD'),
                        toDate: toDate.format('YYYY-MM-DD'),
                    },
                    fetchPolicy: 'network-only',
                }),
                getCountryMetrics({
                    variables: {
                        request: {
                            fromDate: fromDate.toDate(),
                            toDate: toDate.toDate(),
                            company: ['A1'],
                        },
                    },
                    fetchPolicy: 'network-only',
                }),
            ]);

            const reportingSnapshot = cfoResponse?.data?.getCFOReporting ?? null;
            const totalReportingSnapshot = totalCfoResponse?.data?.getCFOReporting ?? null;
            const countrySnapshot = metricsResponse?.data?.allActivePartnerCountryMetrics ?? [];

            setReportingData(reportingSnapshot);
            setTotalReportingData(totalReportingSnapshot);
            setCountryMetrics(countrySnapshot);

            const cacheEntry: BmwPerformanceCacheEntry = {
                updatedAt: Date.now(),
                snapshot: {
                    reportingData: reportingSnapshot,
                    totalReportingData: totalReportingSnapshot,
                    countryMetrics: countrySnapshot,
                },
            };
            globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } finally {
            setIsLoading(false);
        }
    }, [getCacheKey, getCfoReporting, getCountryMetrics, selectedRange]);

    useEffect(() => {
        loadDashboard().catch(() => {
            setReportingData(null);
            setTotalReportingData(null);
            setCountryMetrics([]);
            setIsLoading(false);
        });
    }, [loadDashboard]);

    const totalInvoicesAll = Number(totalReportingData?.totalInvoices?.value ?? 0);
    const totalInvoicesA1 = Number(reportingData?.totalInvoices?.value ?? 0);

    const activePartnersA1 = useMemo(() => {
        const total = (countryMetrics ?? []).reduce((sum, metric) => sum + Number(metric?.coverage?.activePartners ?? 0), 0);
        return total;
    }, [countryMetrics]);

    const statusRows = useMemo(() => {
        return (reportingData?.statusBreakdown ?? [])
            .map((row, index) => ({
                key: `${row.status || 'UNKNOWN'}-${index}`,
                label: row.status || getI18n('bmw-einvoicing-performance-dashboard-unknown-status', 'Unknown', intl),
                value: Number(row.count ?? 0),
            }))
            .sort((a, b) => b.value - a.value);
    }, [intl, reportingData?.statusBreakdown]);

    const maxStatusValue = useMemo(() => {
        const rowValues = statusRows.map((row) => row.value);
        const maxValue = Math.max(...rowValues, 1);
        return maxValue;
    }, [statusRows]);

    return (
        <Spin spinning={isLoading} size="large">
            <div className={classNames(styles.page, theme === 'light' ? styles.pageLight : styles.pageDark)}>
                <div className={styles.shell}>
                    <div className={styles.toolbar}>
                        <Button icon={<BulbOutlined />} onClick={toggleTheme}>
                            {theme === 'dark'
                                ? getI18n('cfo-reporting-switch-light', 'Switch to light view', intl)
                                : getI18n('cfo-reporting-switch-dark', 'Switch to dark view', intl)}
                        </Button>
                        <RangePicker
                            value={selectedRange}
                            onChange={(values) => {
                                if (values?.[0] && values?.[1]) {
                                    setSelectedRange([values[0], values[1]]);
                                }
                            }}
                            allowClear={false}
                            format="MMM D, YYYY"
                            locale={getRangePickerLocalization(intl, globalThis.localStorage.getItem('locale') || 'en')}
                        />
                        <Button icon={<ReloadOutlined />} onClick={() => loadDashboard(true)}>
                            {getI18n('cfo-reporting-refresh', 'Refresh', intl)}
                        </Button>
                    </div>

                    <div className={styles.headerBar}>
                        {getI18n('bmw-einvoicing-performance-dashboard-title', 'BMW eInvoice Performance Dashboard', intl)}
                    </div>

                    <div className={styles.topRow}>
                        <Card className={styles.metricCard} bordered={false}>
                            <div className={styles.metricLabel}>
                                {getI18n('bmw-einvoice-performance-total-invoices', 'Total Invoices', intl)}
                            </div>
                            <div className={styles.metricValue}>{formatCompactNumber(totalInvoicesAll)}</div>
                        </Card>

                        <Card className={styles.metricCard} bordered={false}>
                            <div className={styles.metricLabel}>
                                {getI18n('bmw-einvoice-performance-total-invoices-a1', 'Total Invoices Germany A1', intl)}
                            </div>
                            <div className={styles.metricValue}>{formatCompactNumber(totalInvoicesA1)}</div>
                        </Card>

                        <Card className={styles.metricCard} bordered={false}>
                            <div className={styles.metricLabel}>
                                {getI18n('bmw-einvoice-performance-active-partners', 'Active BMW Partners', intl)}
                            </div>
                            <div className={styles.metricValue}>{formatCompactNumber(activePartnersA1)}</div>
                        </Card>
                    </div>

                    <Card className={styles.statusCard} bordered={false}>
                        <div className={styles.statusTitle}>{getI18n('bmw-einvoice-performance-status-title', 'eInvoices by Status', intl)}</div>
                        <div className={styles.statusList}>
                            {statusRows.map((row) => {
                                const widthPercent = Math.max(4, Math.round((row.value / maxStatusValue) * 100));
                                const widthBucket = getWidthBucket(widthPercent);
                                return (
                                    <div className={styles.statusRow} key={row.key}>
                                        <div className={styles.statusLabel}>{row.label}</div>
                                        <div className={styles.statusTrack}>
                                            <div className={classNames(styles.statusFill, styles[`statusFill${widthBucket}`])} />
                                        </div>
                                        <div className={styles.statusValue}>{formatCompactNumber(row.value)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                </div>
            </div>
        </Spin>
    );
};

export default BMWEinvoicingPerformanceDashboard;
